/**
 * Spotlight collection slider — horizontal scroll-snap (like values) + phone reveal.
 */
(function () {
  function reveal(section) {
    section.classList.add('is-inview');
  }

  function trackOf(section) {
    return section.querySelector('.auracase-spotlight__track');
  }

  function slidesOf(section) {
    return Array.from(section.querySelectorAll('.auracase-spotlight__slide'));
  }

  function dotsOf(section) {
    return Array.from(section.querySelectorAll('.auracase-spotlight__dot'));
  }

  function indexFromScroll(track) {
    var slides = Array.from(track.querySelectorAll('.auracase-spotlight__slide'));
    if (!slides.length) return 0;
    var origin = slides[0].offsetLeft;
    var x = track.scrollLeft + origin;
    var best = 0;
    var bestDist = Infinity;
    for (var i = 0; i < slides.length; i += 1) {
      var dist = Math.abs(slides[i].offsetLeft - x);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    }
    return best;
  }

  function currentIndex(section) {
    var raw = parseInt(section.dataset.currentSlide || '0', 10);
    return Number.isFinite(raw) ? raw : 0;
  }

  function setActive(section, index, animatePhones) {
    var slides = slidesOf(section);
    if (!slides.length) return;
    var next = Math.max(0, Math.min(slides.length - 1, index));
    var changed = String(next) !== section.dataset.currentSlide;
    section.dataset.currentSlide = String(next);

    slides.forEach(function (slide, i) {
      var active = i === next;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
    });

    dotsOf(section).forEach(function (dot, i) {
      var active = i === next;
      dot.classList.toggle('is-active', active);
      if (active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });

    if (animatePhones && changed && section.classList.contains('is-inview')) {
      section.classList.remove('is-inview');
      void section.offsetWidth;
      section.classList.add('is-inview');
    }
  }

  function goTo(section, index, behavior) {
    var track = trackOf(section);
    var slides = slidesOf(section);
    if (!track || !slides.length) return;

    var next = ((index % slides.length) + slides.length) % slides.length;
    var from = currentIndex(section);
    var scrollBehavior = behavior || 'smooth';

    // Wrapping last → first (or reverse) looks wrong if we scroll through the middle
    if (Math.abs(next - from) > 1) scrollBehavior = 'auto';

    section._spotlightProgrammatic = true;
    var origin = slides[0].offsetLeft;
    track.scrollTo({
      left: Math.max(0, slides[next].offsetLeft - origin),
      behavior: scrollBehavior,
    });
    setActive(section, next, true);

    window.clearTimeout(section._spotlightProgTimer);
    section._spotlightProgTimer = window.setTimeout(function () {
      section._spotlightProgrammatic = false;
    }, scrollBehavior === 'smooth' ? 500 : 50);
  }

  function stopAutoplay(section) {
    if (section._spotlightTimer) {
      window.clearInterval(section._spotlightTimer);
      section._spotlightTimer = 0;
    }
  }

  function startAutoplay(section) {
    stopAutoplay(section);
    if (section.dataset.autoplay !== 'true') return;
    if (slidesOf(section).length < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var speed = Math.max(3, parseInt(section.dataset.speed || '6', 10)) * 1000;
    section._spotlightTimer = window.setInterval(function () {
      goTo(section, currentIndex(section) + 1);
    }, speed);
  }

  function bindSlider(section) {
    if (section.dataset.spotlightSliderReady === 'true') return;
    section.dataset.spotlightSliderReady = 'true';
    section.dataset.currentSlide = '0';

    var track = trackOf(section);
    if (!track) return;

    var slides = slidesOf(section);
    if (slides.length < 2) {
      setActive(section, 0, false);
      return;
    }

    var scrollTimer = 0;
    track.addEventListener(
      'scroll',
      function () {
        if (section._spotlightProgrammatic) return;
        stopAutoplay(section);
        window.clearTimeout(scrollTimer);
        scrollTimer = window.setTimeout(function () {
          setActive(section, indexFromScroll(track), true);
          startAutoplay(section);
        }, 60);
      },
      { passive: true }
    );

    track.addEventListener('scrollend', function () {
      if (section._spotlightProgrammatic) {
        section._spotlightProgrammatic = false;
        return;
      }
      setActive(section, indexFromScroll(track), true);
    });

    dotsOf(section).forEach(function (dot) {
      dot.addEventListener('click', function () {
        var idx = parseInt(dot.dataset.slideIndex || '0', 10);
        goTo(section, idx);
        startAutoplay(section);
      });
    });

    section.addEventListener('mouseenter', function () {
      stopAutoplay(section);
    });
    section.addEventListener('mouseleave', function () {
      startAutoplay(section);
    });
    section.addEventListener('focusin', function () {
      stopAutoplay(section);
    });
    section.addEventListener('focusout', function (event) {
      if (!section.contains(event.relatedTarget)) startAutoplay(section);
    });

    setActive(section, indexFromScroll(track), false);
    startAutoplay(section);
  }

  function initSection(section) {
    if (!section || section.dataset.spotlightReady === 'true') return;
    section.dataset.spotlightReady = 'true';

    bindSlider(section);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      reveal(section);
      return;
    }

    if (!('IntersectionObserver' in window)) {
      reveal(section);
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          reveal(entry.target);
          observer.unobserve(entry.target);
        });
      },
      {
        threshold: 0.28,
        rootMargin: '0px 0px -8% 0px',
      }
    );

    observer.observe(section);
  }

  function init() {
    document.querySelectorAll('.auracase-spotlight').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var section =
      event.target && event.target.querySelector
        ? event.target.querySelector('.auracase-spotlight')
        : null;
    if (section) {
      section.dataset.spotlightReady = '';
      section.dataset.spotlightSliderReady = '';
      stopAutoplay(section);
      initSection(section);
    }
  });
})();
